import {useAnimatedPopper} from 'platform/components';
import {Box} from 'platform/foundation';
import styled from 'styled-components';

import {ReactNode, useRef} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {useOnMount} from 'shared/web';

import {PRICE_CHANGE_DETAIL_WIDTH} from '../../../constants/PRICE_CHANGE_DETAIL_WIDTH';

/** Time to move the pointer from the badge onto its detail before the detail closes. */
const PRICE_CHANGE_DETAIL_CLOSE_DELAY_MS = 200;

const PopperTrigger = styled.div`
  display: inline-flex;
  border-radius: ${({theme}) => theme.radii.circular};
  cursor: default;
  user-select: none;

  &:focus-visible {
    outline: none;
    box-shadow: ${({theme}) => theme.shadows.active};
  }
`;

interface PriceChangeBadgeProps {
  detail: ReactNode;
  children: ReactNode;
}

/** The price change badge, with its detail in a popper when there is one. */
export function PriceChangeBadge(props: PriceChangeBadgeProps) {
  const {openPopper, closePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: 'bottom-end',
    usePortal: true,
    autoUpdateOptions: 'default',
  });
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (isNotNil(closeTimeoutRef.current)) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // The detail sits in a portal below the badge, so leaving the badge closes it only after a
  // moment, and the pointer resting on the detail keeps it open.
  const scheduleClose = () => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(closePopper, PRICE_CHANGE_DETAIL_CLOSE_DELAY_MS);
  };

  const open = () => {
    cancelClose();
    openPopper();
  };

  // A pending close must not fire after the badge is gone.
  useOnMount(undefined, cancelClose);

  if (isNil(props.detail)) {
    return <>{props.children}</>;
  }

  return (
    <>
      <PopperTrigger
        ref={referenceRef}
        role="button"
        tabIndex={0}
        aria-label={i18n.t('entity.vehicle.labels.priceChanges')}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={() => closePopper()}
        onClick={open}
      >
        {props.children}
      </PopperTrigger>
      <Popper {...popperProps}>
        <div onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <Box width={['100vw', PRICE_CHANGE_DETAIL_WIDTH]} padding={[4, 0]}>
            {props.detail}
          </Box>
        </div>
      </Popper>
    </>
  );
}
